import { inject, InjectionToken } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ApiService, LassiLayer } from '@core/api/angular';
import { getCustomLayers } from '@core/api/angular/operations/get-custom-layers';
import { getLayers } from '@core/api/angular/operations/get-layers';
import { APPLICATION } from '@lassi/config';
import { isNil } from '@lassi/util';
import { of, shareReplay } from 'rxjs';
import { AuthService } from './auth.service';
import { MapEnquiryOption } from './map';

export interface MapLayer extends LassiLayer {
  group: string;
}

export type ActiveLayer = {
  name: string;
  status: boolean;
  opacity: number;
};
export type ActiveLayers = Array<ActiveLayer>;

export type LassiProfiles = MapEnquiryOption | 'Custom';

export const lassiProfiles: Array<{
  name: LassiProfiles;
  description: string;
}> = [
  {
    name: MapEnquiryOption.PropertyEnquiry,
    description: 'Property',
  },
  {
    name: MapEnquiryOption.SurveyMarkEnquiry,
    description: 'SMES',
  },
  {
    name: MapEnquiryOption.VicnamesEnquiry,
    description: 'VICNAMES',
  },
  {
    name: 'Custom',
    description: 'Custom',
  },
];

export function isLayerInactive(
  { maxScale, minScale }: Pick<LassiLayer, 'minScale' | 'maxScale'>,
  scale: number,
) {
  return (
    (!isNil(maxScale) && scale > maxScale) ||
    (!isNil(minScale) && scale < minScale)
  );
}

export function isMapLayerInactive(layer?: MapLayer, scale?: number) {
  if (isNil(scale) || isNil(layer)) {
    return false;
  }
  return isLayerInactive(layer, scale);
}

export const LassiCustomPreset = new InjectionToken('LassiCustomPreset', {
  factory: () => {
    const api = inject(ApiService);
    const auth = inject(AuthService);
    return rxResource({
      params: () => (auth.isLoggedIn() ? auth.userId() : null),
      stream: ({ params: userId }) => {
        if (!userId) {
          return of(null);
        }

        return api.invoke(getCustomLayers, {
          userId,
        });
      },
    });
  },
});

export const LassiLayersList = new InjectionToken('LassiLayersList', {
  factory: () => {
    const api = inject(ApiService);
    const applicationName = inject(APPLICATION);
    return api
      .invoke(getLayers, {
        applicationName,
      })
      .pipe(shareReplay(1));
  },
});

/**
 * Looks like "layer1:1:0.5,layer2:0:1"
 * @param str
 * @private
 */
export function parseUrlLayers(str: unknown): ActiveLayers | null {
  const urlRegex = /^((\w*:[1|0]:[1|0](.\d)?),?)+$/;

  if (typeof str === 'string' && str && urlRegex.test(str)) {
    return str
      .split(',')
      .map((section) => section.split(':'))
      .map(([name, status, opacity]) => ({
        name,
        status: status === '1',
        opacity: parseFloat(opacity),
      }));
  } else if (str === '') {
    return [];
  }

  return null;
}

export function stringifyUrlLayers(layers: ActiveLayers) {
  return layers
    .map(({ name, status, opacity }) => `${name}:${status ? 1 : 0}:${opacity}`)
    .join(',');
}
